import {
  GenericGeolocationRejection,
  GeolocationError,
  Rule,
} from '@fanduel/geolocation-sdk';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GeolocationProgress } from '@/features/geolocation/types';
import { RootState } from '@/store/store';

export type GeolocationState = {
  progress: GeolocationProgress;
  error?: GeolocationError;
  jwt?: string;
  rejection?: GenericGeolocationRejection<Rule>;
  verified: boolean;
  previouslyVerified: boolean;
  serviceName?: string;
};

const initialState: GeolocationState = {
  progress: GeolocationProgress.NotStarted,
  verified: false,
  previouslyVerified: false,
};

const geolocationSlice = createSlice({
  name: 'geolocation',
  initialState,
  reducers: {
    startGeolocation: (state) => {
      state.progress = GeolocationProgress.InProgress;
    },
    geolocationInvalidated: (state) => {
      state.progress = GeolocationProgress.Invalidated;
    },
    geolocationPassed: (state, action: PayloadAction<string>) => {
      if (state.verified) {
        state.previouslyVerified = true;
      }
      if (action.payload) {
        state.jwt = action.payload;
      }
      state.progress = GeolocationProgress.Success;
      state.verified = true;
    },
    geolocationRejected: (
      state,
      action: PayloadAction<GenericGeolocationRejection<Rule>>,
    ) => {
      state.rejection = action.payload;
      state.progress = GeolocationProgress.Failed;
    },
    geolocationFailure: (state, action: PayloadAction<GeolocationError>) => {
      state.error = action.payload;
      state.progress = GeolocationProgress.Failed;
    },
    resetGeolocationState: () => initialState,
  },
});

export const {
  geolocationFailure,
  geolocationPassed,
  geolocationRejected,
  startGeolocation,
  geolocationInvalidated,
  resetGeolocationState,
} = geolocationSlice.actions;

export const geolocationSelector = (state: RootState) => state.geolocation;

export const geolocationProgressSelector = createSelector(
  geolocationSelector,
  (state) => state.progress,
);

export const geolocationVerifiedSelector = createSelector(
  geolocationSelector,
  (state) => state.verified,
);

export const geolocationPreviouslyVerifiedSelector = createSelector(
  geolocationSelector,
  (state) => state.previouslyVerified,
);

export const geolocationJwtSelector = createSelector(
  geolocationSelector,
  (state) => state.jwt,
);

export const geolocationReducer = geolocationSlice.reducer;
